/**
 * IdleLockOverlay — HIPAA session-lock (iter-77).
 *
 * Blurs the entire dashboard after 15 minutes of no user input and
 * demands password re-entry to unlock.  The user's auth session
 * stays alive on the backend (so no data reload is required on
 * unlock); only the UI is gated.
 *
 * Why not "log the user out after 15 min"?
 *   - Users lose in-flight work (drafts, half-written notes).
 *   - Encourages therapists to leave the tab open and re-login on
 *     every context switch — actually worsening the "left the
 *     laptop open" attack surface.
 * A blur-lock preserves work and forces re-authentication only for
 * VIEWING — the sweet spot for a shared-workstation HIPAA posture.
 *
 * Config:
 *   IDLE_MINUTES        — inactivity threshold (default 15).
 *   WARN_SECONDS_BEFORE — warning banner appears N seconds before
 *                         the actual lock (default 60).
 *
 * All three are read from ``organization.settings.idle_lock`` at
 * mount time with the constants as fallbacks.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { toast as sonnerToast } from 'sonner';
import { Lock, ShieldAlert, Loader2, LogOut, Clock, Hand } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';

const DEFAULT_IDLE_MINUTES = 15;
const DEFAULT_WARN_SECONDS = 60;

// Events treated as "user is here" — mousemove is throttled to
// once per second in the listener to keep this cheap.
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'wheel'];

const now = () => Date.now();

export default function IdleLockOverlay({
  user,
  token,
  onForceLogout,
  onReauthenticate,
  idleMinutes = DEFAULT_IDLE_MINUTES,
  warnSecondsBefore = DEFAULT_WARN_SECONDS,
  disabled = false,
}) {
  const [locked, setLocked] = useState(false);
  const [lockReason, setLockReason] = useState('idle');  // 'idle' | 'manual'
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [cooldownSec, setCooldownSec] = useState(0);

  const lastActivityRef = useRef(now());
  const tickerRef = useRef(null);

  const idleMs = useMemo(() => idleMinutes * 60 * 1000, [idleMinutes]);
  const warnMs = useMemo(() => warnSecondsBefore * 1000, [warnSecondsBefore]);

  // ── Activity listener (throttled) ────────────────────────────────
  useEffect(() => {
    if (disabled) return;
    let lastFired = 0;
    const bump = () => {
      const t = now();
      if (t - lastFired < 1000) return; // throttle: 1 write per second
      lastFired = t;
      lastActivityRef.current = t;
      // If we were in the warning state but the user came back, clear it.
      if (warning && !locked) setWarning(false);
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [disabled, warning, locked]);

  // ── Idle ticker — runs every 5s while active, every 1s in warning ──
  useEffect(() => {
    if (disabled || locked) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
      return;
    }
    const interval = warning ? 1000 : 5000;
    tickerRef.current = setInterval(() => {
      const idleFor = now() - lastActivityRef.current;
      const untilLock = idleMs - idleFor;
      if (untilLock <= 0) {
        setLocked(true);
        setLockReason('idle');
        setWarning(false);
        setRemaining(0);
      } else if (untilLock <= warnMs) {
        setWarning(true);
        setRemaining(Math.ceil(untilLock / 1000));
      } else {
        // Not close to lock — nothing to do
        if (warning) setWarning(false);
      }
    }, interval);
    return () => clearInterval(tickerRef.current);
  }, [disabled, locked, warning, idleMs, warnMs]);

  // ── Manual lock ("Lock now" from user menu) — public via window ──
  useEffect(() => {
    if (disabled) return;
    const forceLock = (reasonOrEvent) => {
      // Callers can pass either a plain string ('manual') via
      // window.__counselcore_force_idle_lock('manual') OR a CustomEvent from
      // the `counselcore:force-idle-lock` window event. Extract the reason
      // defensively so both paths work.
      let reason = 'manual';
      if (typeof reasonOrEvent === 'string') reason = reasonOrEvent;
      else if (reasonOrEvent?.detail?.reason) reason = reasonOrEvent.detail.reason;
      setLockReason(reason);
      setLocked(true);
    };
    window.__counselcore_force_idle_lock = forceLock;
    window.addEventListener('counselcore:force-idle-lock', forceLock);
    return () => {
      delete window.__counselcore_force_idle_lock;
      window.removeEventListener('counselcore:force-idle-lock', forceLock);
    };
  }, [disabled]);

  const handleUnlock = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!password) return setError('Enter your password.');
    if (cooldownSec > 0) return;
    setSubmitting(true);
    setError('');
    try {
      // iter-91 · onReauthenticate now returns an object so we can surface
      // 429 rate-limit responses to the user. Legacy boolean return is
      // still accepted for older wrappers.
      const result = await onReauthenticate?.(password);
      const ok = result === true || result?.success === true;
      if (ok) {
        lastActivityRef.current = now();
        setLocked(false);
        setWarning(false);
        setPassword('');   // wipe the input buffer immediately on success
        setCooldownSec(0);
        sonnerToast.success('Session unlocked.');
      } else if (result && result.status === 429) {
        setError(result.detail || 'Too many failed attempts. Please wait.');
        setCooldownSec(result.retryAfter || 60);
      } else {
        setError('Incorrect password.');
      }
    } catch (err) {
      setError(err?.message || 'Unlock failed.');
    } finally {
      setSubmitting(false);
    }
  }, [password, onReauthenticate, cooldownSec]);

  // iter-91 · Auto-wipe timer — even if the user leaves the lock screen
  // untouched after a failed attempt, drop the typed password from the JS
  // heap 5 seconds after the last keystroke so it doesn't linger in memory
  // on a walked-away workstation.
  useEffect(() => {
    if (!locked || !password) return;
    const t = setTimeout(() => setPassword(''), 5000);
    return () => clearTimeout(t);
  }, [locked, password]);

  // iter-91 · Cooldown countdown — ticks the 429 retry timer once per second
  useEffect(() => {
    if (cooldownSec <= 0) return;
    const t = setTimeout(() => setCooldownSec((v) => Math.max(0, v - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldownSec]);

  // ── Escape hatch: 3 failed attempts → force full logout ──
  const failuresRef = useRef(0);
  useEffect(() => {
    if (error === 'Incorrect password.') {
      failuresRef.current += 1;
      if (failuresRef.current >= 3) {
        sonnerToast.error('Too many failed attempts — logging out.');
        setTimeout(() => onForceLogout?.(), 1500);
      }
    } else if (!error) {
      failuresRef.current = 0;
    }
  }, [error, onForceLogout]);

  if (disabled) return null;

  // ── Warning banner (not locked yet) ─────────────────────────────
  // 2026-07-28 HIPAA fix — portal both banner and full-lock into document.body
  // so `fixed inset-0` covers the true viewport regardless of ancestor CSS
  // transforms/filters (which otherwise scope `fixed` to that ancestor and
  // produce a partial overlay that leaves PHI visible).
  if (warning && !locked) {
    return ReactDOM.createPortal(
      <div
        role="alert"
        aria-live="polite"
        className="fixed top-4 right-4 z-[9998] max-w-sm bg-amber-50 border border-amber-300 rounded-lg shadow-lg p-4"
        data-testid="idle-lock-warning"
      >
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-900">
              Session will lock in {remaining}s
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Move your mouse or press any key to stay signed in.
            </p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ── Full lock overlay ──────────────────────────────────────────
  if (locked) {
    // 2026-07-28 (iter-2) — Some app-wide CSS rules (mobile-responsive.css
    // and utility overrides) were applying transform/z-index/scale to
    // *any* fixed-positioned element, which shrunk the overlay to 90% of
    // the viewport and offset it by ~half its size — leaving PHI visible.
    // We drop Tailwind classes for positioning/sizing and use inline styles
    // (highest CSS specificity short of !important) with explicit
    // `transform: none` to defeat the transform override.
    const overlayStyle = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',        // defeat accessibility.css [role=dialog] cap
      maxHeight: '100vh',       // defeat accessibility.css [role=dialog] cap
      minWidth: '100vw',
      minHeight: '100vh',
      zIndex: 2147483647,        // max int32 — beats any competing z-index
      transform: 'none',
      backgroundColor: 'rgba(15, 23, 42, 0.97)',  // slate-900 at 97%
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: 0,
      padding: 0,
      inset: 0,
      overflow: 'auto',
    };
    return ReactDOM.createPortal(
      <div
        style={overlayStyle}
        data-testid="idle-lock-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="idle-lock-title"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
              <Lock className="w-7 h-7 text-indigo-600" />
            </div>
            <h2 id="idle-lock-title" className="text-xl font-bold text-gray-900">
              Session locked
            </h2>
            {/* iter-91 · Lock-reason badge — tells the user WHY they were
                locked so a manual lock (e.g. coworker walking up) doesn't
                get mistaken for an idle timeout. */}
            <div
              className={`mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                lockReason === 'manual'
                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                  : 'bg-amber-50 text-amber-800 border border-amber-200'
              }`}
              data-testid="idle-lock-reason"
              data-reason={lockReason}
            >
              {lockReason === 'manual'
                ? <><Hand className="w-3 h-3" /> Locked manually</>
                : <><Clock className="w-3 h-3" /> Idle timeout</>}
            </div>
            <p className="text-sm text-gray-500 mt-3">
              {lockReason === 'manual'
                ? 'You locked this session. Re-enter your password to continue.'
                : `You've been inactive for ${idleMinutes} minutes. Re-enter your password to protect client data on shared devices.`}
            </p>
          </div>
          <form
            onSubmit={handleUnlock}
            className="space-y-4"
            autoComplete="off"
            data-lpignore="true"
            data-form-type="other"
          >
            <div>
              <Label htmlFor="idle-lock-user" className="text-xs text-gray-500">
                Signed in as
              </Label>
              <Input
                id="idle-lock-user"
                type="text"
                value={user?.email || ''}
                disabled
                readOnly
                className="bg-gray-50"
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="idle-lock-password">Password</Label>
              {/* HIPAA session-lock — password field is intentionally
                  hardened against browser password managers and reveal:
                  • name="idle-lock-secret" (not "password") → browsers won't
                    match it against saved credentials for this origin
                  • autoComplete="new-password" is the widely-honored escape
                    hatch that suppresses autofill + the "save password" prompt
                  • type="password" always — no eye toggle. Anyone with
                    shoulder-surfing access should not be able to read the
                    admin credentials from the lock screen on a shared device
                  • spellCheck / autoCorrect / autoCapitalize all off */}
              <Input
                id="idle-lock-password"
                name="idle-lock-secret"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                autoFocus
                autoComplete="new-password"
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
                data-lpignore="true"
                data-form-type="other"
                data-testid="idle-lock-password"
                disabled={submitting}
              />
            </div>
            {error && (
              <Alert className="bg-rose-50 border-rose-200">
                <AlertDescription className="text-rose-800 text-sm">{error}</AlertDescription>
              </Alert>
            )}
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={submitting || !password || cooldownSec > 0}
                data-testid="idle-lock-unlock"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Unlocking…</>
                  : cooldownSec > 0
                    ? <><Clock className="w-4 h-4 mr-2" /> Wait {cooldownSec}s</>
                    : <><Lock className="w-4 h-4 mr-2" /> Unlock</>}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onForceLogout}
                data-testid="idle-lock-logout"
                className="text-gray-700"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </Button>
            </div>
          </form>
        </div>
      </div>,
      document.body
    );
  }

  return null;
}
